import type { ReactNode } from "react";
import { SlideStatement } from "../../../SlideStatement";
import { SupLink } from "../../../SlideContext";

export default function TheMarket() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-10 md:[&_header]:mb-0">
      <SlideStatement eyebrow="The ASO Market">
        <div>
          <h1 className="text-3xl font-semibold leading-[1.05] tracking-tight md:text-7xl">
            ASO software is a $1B category today.{" "}
            <span className="text-blue-700">$7.6B by 2035.</span>
          </h1>
          {/* Restates the claim's stake rather than supporting it. */}
          <p className="mt-4 text-lg font-medium tracking-tight text-slate-600 md:mt-5 md:text-2xl">
            That is what we are carving our share out of.
          </p>
        </div>
      </SlideStatement>

      <div className="flex flex-col md:min-h-0 md:flex-1 md:justify-between md:pt-6">
        {/* Funnel — TAM → SAM → SOM, narrowing left to right */}
        <div className="flex flex-col items-stretch gap-3 md:flex-row md:gap-0">
        {/* TAM */}
        <FunnelCard tier="TAM" sub="Top-down · category" emphasized>
          <p className="text-3xl font-bold tracking-tight text-blue-700 md:text-[2.75rem] md:leading-none">
            $1.9–7.6B
          </p>
          <div className="mt-3 flex flex-1 flex-col justify-center gap-2 md:mt-4">
            <div className="flex items-baseline justify-between gap-2 border-t border-blue-100 pt-2">
              <p className="text-sm font-semibold text-slate-900">Today</p>
              <span className="shrink-0 text-base font-bold tabular-nums text-slate-900 md:text-lg">
                $0.5–1.0B
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-2 border-t border-blue-100 pt-2">
              <p className="text-sm font-semibold text-slate-900">Growth</p>
              <span className="shrink-0 text-base font-bold tabular-nums text-blue-700 md:text-lg">
                12.5–20.7% CAGR
              </span>
            </div>
          </div>
          <p className="mt-3 text-[11px] leading-snug text-slate-500 md:text-xs">
            Four independent ASO-software reports.
            <SupLink to="sources">8,9,10,11</SupLink> Scope
            <SupLink to="sources">12</SupLink>
          </p>
        </FunnelCard>

        <Connector />

        {/* SAM — emphasized */}
        <FunnelCard tier="SAM" sub="Bottom-up · serviceable">
          <p className="text-2xl font-bold tracking-tight text-slate-900 md:text-[2rem] md:leading-none">
            €239M
          </p>
          <div className="mt-3 flex flex-1 flex-col justify-center gap-2 md:mt-4">
            <SamRow
              seg="Publishers"
              math="100K × €184/mo × 12"
              value="€221M"
            />
            <SamRow
              seg="Agencies & pros"
              math="~3K × €500/mo × 12"
              value="€18M"
            />
          </div>
        </FunnelCard>

        <Connector />

        {/* SOM */}
        <FunnelCard tier="SOM" sub="Our path · publisher share">
          <div className="flex flex-1 flex-col justify-center gap-1.5">
            <SomRung pct="0.7% · 700 accts" value="€1.55M" note="m12 target" />
            <SomRung pct="5% · 5K accts" value="€11M" />
            <SomRung pct="20% · 20K accts" value="€44M" />
          </div>
          <p className="mt-3 text-[11px] leading-snug text-slate-500 md:text-xs">
            End-of-period run-rate ARR at increasing penetration.
          </p>
        </FunnelCard>
      </div>

      {/* Adjacent-TAM line — the beachhead numbers above are not the ceiling
          (companion edit required by slide 16's beachhead framing) */}
      {/* Proof strip — the population & category are real */}
      <div className="mt-5 grid grid-cols-1 gap-x-8 gap-y-4 border-t border-slate-200 pt-4 md:mt-6 md:grid-cols-3 md:pt-5">
        <Proof value="1M+" tint="slate" label="Active publishers globally">
          848K App Store · 615K Play
          <SupLink to="sources">1</SupLink>, less ≈30% cross-publishing overlap
        </Proof>
        <Proof value="5K+" tint="slate" label="ASO specialists & agencies">
          ~400 agencies (Phiture, Gummicube, Yodel) · ~3K consultants
          <SupLink to="sources">14</SupLink>
        </Proof>
        <Proof value="$1B+" tint="blue" label="Category M&amp;A and VC">
          Since 2020: Sensor Tower ← data.ai (2024) · AppTweak Series B · AppFollow VC-backed
          <SupLink to="sources">13</SupLink>
        </Proof>
      </div>

      </div>
    </div>
  );
}

/* ---------- Funnel ---------- */

function FunnelCard({
  tier,
  sub,
  emphasized,
  children,
}: {
  tier: string;
  sub: string;
  emphasized?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={`flex flex-1 flex-col rounded-2xl p-4 ring-1 md:p-5 ${
        emphasized
          ? "bg-blue-50 ring-blue-200"
          : "bg-white ring-slate-200"
      }`}
    >
      <div className="mb-3 md:mb-4">
        <p className="text-xl font-bold tracking-tight text-blue-700 md:text-2xl">
          {tier}
        </p>
        <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-slate-500 md:text-[11px]">
          {sub}
        </p>
      </div>
      {children}
    </div>
  );
}

function Connector() {
  return (
    <div className="flex items-center justify-center py-1 md:px-2 md:py-0">
      <span className="text-xl text-slate-300 md:text-2xl">→</span>
    </div>
  );
}

function SamRow({
  seg,
  math,
  value,
}: {
  seg: string;
  math: string;
  value: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2 border-t border-blue-100 pt-2">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-900">{seg}</p>
        <p className="text-[11px] leading-tight text-slate-500 md:text-xs">
          {math}
        </p>
      </div>
      <span className="shrink-0 text-base font-bold tabular-nums text-slate-900 md:text-lg">
        {value}
      </span>
    </div>
  );
}

function SomRung({
  pct,
  value,
  note,
}: {
  pct: string;
  value: string;
  note?: string;
}) {
  return (
    <div
      className={`flex items-baseline justify-between gap-2 rounded-lg px-2.5 py-1.5 ${
        note ? "bg-blue-50 ring-1 ring-blue-200" : ""
      }`}
    >
      <span className="text-sm text-slate-700">
        {pct}
        {note ? (
          <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700">
            {note}
          </span>
        ) : null}
      </span>
      <span
        className={`shrink-0 text-base font-bold tabular-nums md:text-lg ${
          note ? "text-blue-700" : "text-slate-900"
        }`}
      >
        {value}
      </span>
    </div>
  );
}

/* ---------- Proof strip ---------- */

function Proof({
  value,
  label,
  tint,
  children,
}: {
  value: string;
  label: string;
  tint: "slate" | "blue";
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col">
      <div className="flex items-baseline gap-2.5">
        <p
          className={`text-2xl font-bold tracking-tight md:text-3xl ${
            tint === "blue" ? "text-blue-700" : "text-slate-900"
          }`}
        >
          {value}
        </p>
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-blue-700 md:text-[13px]">
          {label}
        </p>
      </div>
      <p className="mt-1 text-xs leading-snug text-slate-500 md:text-sm">
        {children}
      </p>
    </div>
  );
}

